/**
 * The subset of drizzle's built `defineRelations(...)` output this resolver
 * reads.
 */
export type RelationsMetadata<TTable = unknown> = Record<
  string,
  {
    relations: Record<string, { targetTableName: string }>;
    table: TTable;
  }
>;

type WithClause = Record<string, unknown>;

function isWithBearingConfig(value: unknown): value is { with?: WithClause } {
  return typeof value === "object" && value !== null;
}

export function getWithClause(config: unknown): WithClause | undefined {
  if (!isWithBearingConfig(config)) {
    return undefined;
  }
  const { with: withClause } = config;
  return isWithBearingConfig(withClause) ? withClause : undefined;
}

/**
 * Returns the SQL table names a relational read depends on: its main table plus
 * every table reached through its `with` graph.
 */
export function resolveTableChangeTables<TTable>(
  relations: RelationsMetadata<TTable>,
  getSqlTableName: (table: TTable) => string,
  mainTableKey: string,
  withClause: WithClause | undefined,
): string[] {
  const tableNames = new Set<string>();

  const visit = (tableKey: string, clause: WithClause | undefined): void => {
    const entry = relations[tableKey];
    if (entry === undefined) {
      return;
    }
    tableNames.add(getSqlTableName(entry.table));

    if (clause === undefined) {
      return;
    }
    for (const [relationName, relationValue] of Object.entries(clause)) {
      if (relationValue === false || relationValue === undefined || relationValue === null) {
        continue;
      }
      const targetTableKey = entry.relations[relationName]?.targetTableName;
      if (targetTableKey === undefined) {
        continue;
      }
      const nestedWith = isWithBearingConfig(relationValue) ? relationValue.with : undefined;
      visit(targetTableKey, nestedWith);
    }
  };

  visit(mainTableKey, withClause);
  return [...tableNames];
}
